// ---------------------------------------------- modules import
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FirebaseReducer,
  isEmpty,
  isLoaded,
  useFirestoreConnect
} from "react-redux-firebase";
import { useHistory } from "react-router-dom";

import firebase from "../../config/fbInit";
import { AppState } from "../../store/rootReducer";
import { IUserData, IOrderedPinnedPost } from "../../store/firebase/common";
import PermissionHelper from "../../store/helpers/permissionHelper";
import TagHelper from "../../store/helpers/tagHelper";
import {
  IPinnedPost,
  IReader,
  newPinnedPost
} from "../../store/pinnedPost/common";
import {
  thunkUpdatePinnedPost,
  thunkUnpinPinnedPost
} from "../../store/pinnedPost/thunkActions";

// ---------------------------------------------- reduce ordered data
const reducePinnedPosts = (
  orderedPinnedPosts: IOrderedPinnedPost[],
  profile: FirebaseReducer.Profile<IUserData>
) =>
  orderedPinnedPosts
    .reduce(
      (currentPinnedPosts, orderedPinnedPost) => [
        ...currentPinnedPosts,
        { ...orderedPinnedPost }
      ],
      [] as IPinnedPost[]
    )
    .reduce(
      (currentPinnedPosts, pinnedPost) =>
        TagHelper.isUserTagged(pinnedPost.tags, profile.tags)
          ? [...currentPinnedPosts, { ...pinnedPost }]
          : [...currentPinnedPosts],
      [] as IPinnedPost[]
    );

// ---------------------------------------------- custom hooks
export const useAuth = () => {
  // ---------------------------------------------- redux connect
  const auth = useSelector(
    (state: AppState) =>
      (state.firebase as FirebaseReducer.Reducer<
        Record<string, Record<string | number, string | number>>,
        IUserData
      >).auth
  );
  const profile = useSelector(
    (state: AppState) =>
      (state.firebase as FirebaseReducer.Reducer<
        Record<string, Record<string | number, string | number>>,
        IUserData
      >).profile
  );

  // ---------------------------------------------- local state
  const [areLoaded, setAreLoaded] = useState(
    isLoaded(auth) && isLoaded(profile)
  );
  const [isEmptied, setIsEmptied] = useState(isEmpty(auth) || isEmpty(profile));

  // ---------------------------------------------- effects
  useEffect(() => {
    setAreLoaded(isLoaded(auth) && isLoaded(profile));
    setIsEmptied(isEmpty(auth) || isEmpty(profile));
  }, [auth, profile]);

  return {
    auth,
    profile,
    areLoaded,
    isEmptied
  };
};

export const usePinnedPosts = (profile: FirebaseReducer.Profile<IUserData>) => {
  // ---------------------------------------------- firestore connect
  useFirestoreConnect({
    collection: "pinnedPosts",
    orderBy: ["pinnedAt", "desc"]
  });

  // ---------------------------------------------- redux connect
  const orderedPinnedPosts = useSelector(
    (state: AppState) =>
      state.firestore.ordered["pinnedPosts"] as IOrderedPinnedPost[]
  );

  // ---------------------------------------------- local state
  const [pinnedPosts, setPinnedPosts] = useState<IPinnedPost[]>([]);
  const [fetching, setFetching] = useState<boolean>(
    !isLoaded(orderedPinnedPosts)
  );
  const [error, setError] = useState<string | null>(null);

  // ---------------------------------------------- effects
  useEffect(() => {
    if (isLoaded(orderedPinnedPosts)) {
      if (isEmpty(orderedPinnedPosts)) {
        setError("No posts found.");
      } else {
        const currentPosts = reducePinnedPosts(orderedPinnedPosts, profile);

        setPinnedPosts(currentPosts);
        setError(null);
      }

      setFetching(false);
    } else {
      setFetching(true);
      setError(null);
    }
  }, [orderedPinnedPosts, profile]);

  // ---------------------------------------------- return value
  return {
    pinnedPosts,
    fetching,
    error
  };
};

export const usePinnedPostForDetails = (
  profile: FirebaseReducer.Profile<IUserData>,
  id?: string
) => {
  const dispatch = useDispatch();

  // ---------------------------------------------- router state
  let history = useHistory();

  // ---------------------------------------------- firestore connect
  useFirestoreConnect({ collection: "pinnedPosts" });

  // ---------------------------------------------- redux connect
  const orderedPinnedPosts = useSelector(
    (state: AppState) =>
      state.firestore.ordered["pinnedPosts"] as IOrderedPinnedPost[]
  );

  // ---------------------------------------------- local state
  const [pinnedPost, setPinnedPost] = useState<IPinnedPost>(newPinnedPost());

  // ---------------------------------------------- handlers
  const handleUpdateReaders = (
    profile: FirebaseReducer.Profile<IUserData>,
    readers: IReader[]
  ) => {
    if (profile) dispatch(thunkUpdatePinnedPost({ ...pinnedPost, readers }));
  };

  const handleUnpin = (
    auth: FirebaseReducer.AuthState,
    profile: FirebaseReducer.Profile<IUserData>,
    path: string
  ) => {
    if (
      auth.uid &&
      PermissionHelper.getModify(profile.permissions, pinnedPost.indexOrigin)
    ) {
      dispatch(
        thunkUnpinPinnedPost({
          ...pinnedPost,
          lastModified: firebase.firestore.Timestamp.now()
        })
      );
      history.push(path);
    }
  };

  // ---------------------------------------------- effects
  useEffect(() => {
    if (isLoaded(orderedPinnedPosts) && !isEmpty(orderedPinnedPosts)) {
      const currentPinnedPosts = reducePinnedPosts(orderedPinnedPosts, profile);

      const currentPinnedPost = currentPinnedPosts.find(
        currentPinnedPost => currentPinnedPost.id === id
      );

      if (currentPinnedPost) setPinnedPost(currentPinnedPost);
    }
  }, [orderedPinnedPosts, profile, id]);

  // ---------------------------------------------- return value
  return {
    post: pinnedPost,
    onUpdateReaders: handleUpdateReaders,
    onUnpin: handleUnpin
  };
};
